import { getValidAuthorizationHeader } from '../auth/cognito';
import { getReferralsApiUrl } from '../config/api';
import { ensureSignInUserInfo } from './signinUserInfo';
function normalizeNamedValues(values) {
    if (!values)
        return [];
    return values
        .filter((entry) => typeof entry?.value === 'number')
        .map((entry) => ({
        name: entry.name?.trim() || String(entry.value),
        value: entry.value,
    }));
}
function normalizeLookup(raw) {
    return {
        users: raw.users ?? [],
        sites: raw.sites ?? [],
        issueTypes: raw.issueTypes ?? [],
        personKinds: normalizeNamedValues(raw.personKinds),
        personTypes: normalizeNamedValues(raw.personTypes),
        taskRoles: normalizeNamedValues(raw.taskRoles),
        statuses: normalizeNamedValues(raw.statuses),
    };
}
export function namedValueLabel(values, value, fallbackPrefix = 'Value') {
    return values.find((entry) => entry.value === value)?.name ?? `${fallbackPrefix} ${value}`;
}
/**
 * Enum values are environment-specific, so never hard-code one. Prefer the
 * catch-all "Other" entry, else the first value the environment defines.
 */
export function defaultLookupValue(values) {
    const other = values.find((entry) => entry.name.trim().toLowerCase() === 'other');
    return other?.value ?? values[0]?.value ?? 0;
}
/** Match free text (e.g. an AI-supplied role note) against lookup entry names. */
export function matchLookupValue(text, values) {
    const haystack = text.trim().toLowerCase();
    if (!haystack)
        return null;
    for (const entry of values) {
        const name = entry.name.trim().toLowerCase();
        if (!name)
            continue;
        const wordBoundary = new RegExp(`\\b${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
        if (wordBoundary.test(haystack))
            return entry.value;
    }
    return null;
}
export async function fetchIncidentLookup() {
    const [authorization, userInfo] = await Promise.all([
        getValidAuthorizationHeader(),
        ensureSignInUserInfo().catch(() => null),
    ]);
    const userId = typeof userInfo?.detail?.id === 'number' ? userInfo.detail.id : undefined;
    const response = await fetch(getReferralsApiUrl('/referrals/lookup'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: authorization,
        },
        body: JSON.stringify(userId != null ? { userId } : {}),
    });
    const text = await response.text();
    if (!response.ok) {
        throw new Error(`incident lookup failed (${response.status}): ${text.slice(0, 200)}`);
    }
    let payload;
    try {
        payload = JSON.parse(text);
    }
    catch {
        throw new Error(`incident lookup returned non-JSON: ${text.slice(0, 200)}`);
    }
    if (!payload.lookup) {
        throw new Error(payload.error ?? 'incident lookup response missing lookup');
    }
    return normalizeLookup(payload.lookup);
}
let lookupPromise = null;
let cachedLookup = null;
/** Warm Kokomo /api/external/Incidents/lookup via the portal API (caches server-side). */
export function ensureIncidentLookup() {
    if (!lookupPromise) {
        lookupPromise = fetchIncidentLookup()
            .then((lookup) => {
            cachedLookup = lookup;
            return lookup;
        })
            .catch((err) => {
            lookupPromise = null;
            cachedLookup = null;
            throw err;
        });
    }
    return lookupPromise;
}
/** Sync read of the in-memory client cache (null until ensureIncidentLookup resolves). */
export function getCachedIncidentLookup() {
    return cachedLookup;
}
export function clearIncidentLookupClientCache() {
    lookupPromise = null;
    cachedLookup = null;
}
